import React, { useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import copy from "../../assets/icons/copy.png";
import dot from "../../assets/icons/dot.png";
import {
  setClientHeader,
  setClientFooter,
  createMacro,
  updateMacro,
  isMacroExists,
} from "../../utils/API_SERVICE";
import { IoSearchSharp } from "react-icons/io5";
import { FaStethoscope } from "react-icons/fa";
import { toast } from "react-toastify";
import { Select } from "../common/Select";
import ConfirmModal from "../common/ConfirmModal";
import { useTranslation } from "react-i18next";

/** True if RTE HTML has no visible text (empty, whitespace, or only <br>/&nbsp;) */
function isRichTextEmpty(html) {
  if (html == null) return true;
  const str = String(html);
  if (!str.trim()) return true;
  if (typeof document === "undefined") {
    return !str
      .replace(/<[^>]+>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
      .trim();
  }
  const div = document.createElement("div");
  div.innerHTML = str;
  const text = (div.textContent || div.innerText || "")
    .replace(/\u00a0/g, " ")
    .trim();
  return text.length === 0;
}

const TableDropdown = ({ label, value, options, onChange }) => {
  const { t } = useTranslation('common');
  return (
    <div className="w-full h-12 p-3 rounded-full mt-3 mb-5 relative">
      <select
        className="w-full border border-gray-300 dark:border-gray-600 p-3 rounded-full bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-100 appearance-none focus:outline-none focus:ring-2 focus:ring-green-400 dark:focus:ring-green-600 cursor-pointer"
        value={value}
        onChange={onChange}
      >
        <option value="" disabled>{`${t('select')} ${label}`}</option>
        {options.map((item) => (
          <option value={item} key={item} className="cursor-pointer">
            {item}
          </option>
        ))}
      </select>
      <div className="absolute inset-y-0 right-0 mt-6 flex items-center pr-8 pointer-events-none">
        <svg
          className="w-5 h-5 text-gray-900 dark:text-gray-100"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 9l-7 7-7-7"
          ></path>
        </svg>
      </div>
    </div>
  );
};

function DraggableSection({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: "auto",
    cursor: "default",
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {typeof children === "function" ? children(listeners) : children}
    </div>
  );
}

const Sidebar = ({
  name,
  setName,
  saveTemplate,
  macroData,
  singleFetchedTemplate,
  isMacroEditing,
  isEditing,
  selectedCard,
  catalogTableItems,
  macroContent,
  catalogTableName,
  templateContent = "",
  onDoubleClickItem,
  headerContent,
  footerContent,
  fetchMacros,
  fetchClientHeader,
  fetchClientFooter,
  addMacroAtCursor,
  macroEditorRef,
  addTemplateAtCursor,
  templateEditorRef,
  addHeaderAtCursor,
  headerEditorRef,
  addFooterAtCursor,
  footerEditorRef,
  language = "en",
  anathomy = "",
  setAnathomy,
  anathomies = [],
  templateSettings,
  updateTemplateSettings,
  canManageDefaultTemplates = false,
  canCreateOrEditTemplates = true,
  /** Called after a new macro is created (not after update) so parent can clear draft state. */
  onMacroCreateSuccess,
  /** Resets template editor + sidebar fields (name, content, instructions, anatomy, default toggle, isEditing). */
  onResetForm,
}) => {
  const { t } = useTranslation('common');
  const [sections, setSections] = useState([]);
  const [selectedTable, setSelectedTable] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const [draggingEnabled, setDraggingEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [showNewTemplateConfirm, setShowNewTemplateConfirm] = useState(false);
  const lastAppliedMacroIdRef = useRef(null);

  const SaveMAcroSoBJ = {
    MacroName: name,
    Content: macroContent,
    SpeciesIds: [],
    ModalityTypeIds: [],
    StudyTypeIds: [],
  };

  useEffect(() => {
    if (singleFetchedTemplate) {
      setName(singleFetchedTemplate.templateName);
    }
  }, [
    singleFetchedTemplate,
    setName,
  ]);

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  }, []);

  useEffect(() => {
    // addInformation panel (Species/Modality/Study Type/User ID) not in list - Radical use Wix-style table; kept in render below for future use
    let baseSections = ["addTemplate", "addField"];

    // Radical only: non-admin must not see Create Template form (macros still allowed)
    if (!canCreateOrEditTemplates && selectedCard !== 0) {
      baseSections = baseSections.filter((s) => s !== "addTemplate");
    }

    // Add anathomy section only when Template Content tab is selected (selectedCard === 1)
    if (selectedCard === 1) {
      if (baseSections.includes("addTemplate")) {
        const index = baseSections.indexOf("addTemplate");
        baseSections.splice(index + 1, 0, "anathomy");
      } else {
        baseSections = ["anathomy", ...baseSections];
      }
    }

    setSections(baseSections);
  }, [selectedCard, canCreateOrEditTemplates]);

  useEffect(() => {
    // Update the name field based on the selected card
    if (selectedCard === 2) {
      const headerName = t('templatesHeader');
      if (name !== headerName) {
        setName(headerName);
      }
    } else if (selectedCard === 3) {
      const footerName = t('templatesFooter');
      if (name !== footerName) {
        setName(footerName);
      }
    } else if (selectedCard !== 0 && selectedCard !== 1 && name !== "") {
      // Only clear name if not macro (0) or template (1) card
      setName("");
    }
  }, [selectedCard, t, name, setName]); // Include name to check current value

  useEffect(() => {
    setErrors({});
  }, [selectedCard]);

  useEffect(() => {
    // Filter catalog table items based on the selected table and search query
    setFilteredItems(
      catalogTableItems.filter(
        (item) =>
          item.tableName === selectedTable &&
          item.table_code &&
          item.table_code.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [selectedTable, searchQuery, catalogTableItems]);

  useEffect(() => {
    if (!isMacroEditing) {
      lastAppliedMacroIdRef.current = null;
      return;
    }

    const macroId = macroData?.macroId;
    if (!macroId) return;
    if (lastAppliedMacroIdRef.current === macroId) return;
    lastAppliedMacroIdRef.current = macroId;

    setName(macroData.macroName);
  }, [
    isMacroEditing,
    macroData,
    setName,
  ]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: draggingEnabled ? 5 : Infinity,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setSections((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleFocus = () => setDraggingEnabled(false);
  const handleBlur = () => setDraggingEnabled(true);

  // --- Validation ---
  const validate = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = t("nameRequired");
    // Same message for macro vs template; different editor buffers (0 = macro, 1 = template)
    const editorHtml =
      selectedCard === 1
        ? templateContent
        : selectedCard === 0
          ? macroContent
          : null;
    if (editorHtml != null && isRichTextEmpty(editorHtml)) {
      newErrors.content = t("contentRequired");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveTemplate = async () => {
    if (!validate()) return;
    setSaving(true);
    await saveTemplate();
    setSaving(false);
  };

  /** "+ New Template" — wipe editor + sidebar fields so the user can start fresh.
   *  Opens a confirmation modal when there are unsaved edits so a stray click
   *  can't lose work; resets directly on a clean form. */
  const performResetForm = () => {
    setErrors({});
    onResetForm?.();
  };

  const handleNewTemplate = () => {
    if (!onResetForm) return;
    const hasUnsaved =
      isEditing ||
      (typeof name === "string" && name.trim().length > 0) ||
      !isRichTextEmpty(templateContent) ||
      !!anathomy ||
      !!templateSettings?.isDefault;
    if (hasUnsaved) {
      setShowNewTemplateConfirm(true);
      return;
    }
    performResetForm();
  };

  const handleNewTemplateConfirm = () => {
    setShowNewTemplateConfirm(false);
    performResetForm();
  };

  const handleNewTemplateCancel = () => {
    setShowNewTemplateConfirm(false);
  };

  const handleSaveHeader = async () => {
    try {
      setSaving(true);
      const response = await setClientHeader({ content: headerContent });
      if (response.success) {
        fetchClientHeader(); // Fetch updated client header
        toast.success(t('clientHeaderUpdatedSuccessfully'));
      } else {
        toast.error(t('failedToUpdateClientHeader'));
      }
    } catch (error) {
      toast.error(t('errorUpdatingClientHeader'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFooter = async () => {
    try {
      setSaving(true);
      const response = await setClientFooter({ content: footerContent });
      if (response.success) {
        toast.success(t('clientFooterUpdatedSuccessfully'));
        fetchClientFooter(); // Fetch updated client footer
      } else {
        toast.error(t('failedToUpdateClientFooter'));
      }
    } catch (error) {
      toast.error(t('errorUpdatingClientFooter'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMacro = async () => {
    if (!validate()) return;
    try {
      setSaving(true);
      if (isMacroEditing) {
        // Update existing macro
        await updateMacro(macroData);
        fetchMacros(); // Fetch updated macros
        toast.success(t('macroUpdatedSuccessfully'));
      } else {
        // Check if macro already exists before creating
        const macroExistsResponse = await isMacroExists(name);
        if (
          macroExistsResponse.success &&
          macroExistsResponse.payload.isExists
        ) {
          toast.error(t('macroAlreadyExists', { name }));
          return;
        }
        // Create new macro
        await createMacro(SaveMAcroSoBJ);
        fetchMacros(); // Fetch updated macros
        toast.success(t('macroSavedSuccessfully'));
        onMacroCreateSuccess?.();
      }
    } catch (error) {
      toast.error(t('errorSavingMacro'));
    } finally {
      setSaving(false);
    }
  };

  // Handler for macro/template/header/footer dropdown/field insertion
  const handleInsertAtCursor = (value, type) => {
    let editorRef, addAtCursor;
    if (type === "macro") {
      editorRef = macroEditorRef;
      addAtCursor = addMacroAtCursor;
    } else if (type === "template") {
      editorRef = templateEditorRef;
      addAtCursor = addTemplateAtCursor;
    } else if (type === "header") {
      editorRef = headerEditorRef;
      addAtCursor = addHeaderAtCursor;
    } else if (type === "footer") {
      editorRef = footerEditorRef;
      addAtCursor = addFooterAtCursor;
    }
    if (editorRef && editorRef.current && editorRef.current.editor) {
      const wysiwyg = editorRef.current.editor.core?.context?.element?.wysiwyg;
      if (wysiwyg && typeof wysiwyg.focus === "function") {
        wysiwyg.focus();
      }
    }
    if (addAtCursor) {
      addAtCursor(value);
    }
  };

  // Reusable drag handle: same design as PatientSidebar — top center, 80% inside / 20% outside
  const dragHandleClass =
    "flex absolute top-0 left-1/2 -translate-x-1/2 -translate-y-[20%] w-8 h-8 bg-white dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-700 rounded-full items-center justify-center cursor-grab active:cursor-grabbing z-10 border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 transition-colors touch-manipulation";
  const dragHandleSvg = (
    <svg className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300 shrink-0" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <circle cx="9" cy="6" r="1.5" /><circle cx="15" cy="6" r="1.5" /><circle cx="9" cy="12" r="1.5" /><circle cx="15" cy="12" r="1.5" /><circle cx="9" cy="18" r="1.5" /><circle cx="15" cy="18" r="1.5" />
    </svg>
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={sections}>
        <div className="space-y-1 sm:space-y-2">
          {sections.map((section) => {
            if (section === "addTemplate") {
              return (
                <DraggableSection key={section} id={section}>
                  {(listeners) => (
                    <div className="relative bg-white dark:bg-gray-800 p-2 sm:p-3 rounded-2xl shadow-sm dark:shadow-gray-700">
                      <div
                        className={dragHandleClass}
                        {...listeners}
                        style={{ touchAction: 'none' }}
                        title={t('dragToReorder') || 'Drag to reorder'}
                        role="button"
                        aria-label={t('dragToReorder') || 'Drag to reorder'}
                      >
                        {dragHandleSvg}
                      </div>
                      {loading ? (
                        <>
                          <Skeleton height={40} />
                          <Skeleton height={40} />
                          <Skeleton height={40} />
                        </>
                      ) : (
                        <>
                          <div className="flex gap-2 justify-between items-start mb-3">
                            <div className="flex gap-2 min-w-0">
                              <div className="w-11 h-11 bg-gray-200 dark:bg-gray-700 rounded-full flex justify-center items-center flex-shrink-0">
                                <img
                                  src={copy}
                                  alt=""
                                  className="w-5 h-5 dark:invert"
                                />
                              </div>
                              <div className="text-xs text-gray-900 dark:text-gray-100 min-w-0">
                                <h1 className="font-bold">
                                  {selectedCard === 0
                                    ? t('macro')
                                    : selectedCard === 1
                                    ? t('templateContent')
                                    : selectedCard === 2
                                    ? t('templatesHeader')
                                    : selectedCard === 3
                                    ? t('templatesFooter')
                                    : t('addNewTemplate')}
                                </h1>
                                <p>
                                  {selectedCard === 0
                                    ? t('addMacroName')
                                    : selectedCard === 1
                                    ? t('addNewTemplate')
                                    : selectedCard === 2
                                    ? t('templatesHeader')
                                    : selectedCard === 3
                                    ? t('templatesFooter')
                                    : t('addNewTemplate')}
                                </p>
                              </div>
                            </div>
                            {selectedCard === 1 && canCreateOrEditTemplates && onResetForm && (
                              <button
                                type="button"
                                onClick={handleNewTemplate}
                                title={t('newTemplate')}
                                aria-label={t('newTemplate')}
                                className="flex-shrink-0 inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-[#CBEA7B80] hover:bg-[#CBEA7B] dark:bg-[#A4C63980] dark:hover:bg-[#A4C639] text-gray-900 dark:text-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-green-400 dark:focus:ring-green-600"
                              >
                                <span className="text-base leading-none">+</span>
                                <span className="whitespace-nowrap">{t('newTemplate')}</span>
                              </button>
                            )}
                          </div>
                          <div className="flex gap-2 justify-between">
                            <input
                              type="text"
                              dir={
                                language === "ar" || language === "he"
                                  ? "rtl"
                                  : "ltr"
                              }
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                              onFocus={handleFocus}
                              onBlur={handleBlur}
                              placeholder={
                                selectedCard === 0
                                  ? t('addMacroName')
                                  : t('addNewTemplate')
                              }
                              className="border border-gray-300 dark:border-gray-600 font-poppins rounded-full w-full h-10 px-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-400 dark:focus:ring-green-600"
                              style={{
                                textAlign:
                                  language === "ar" || language === "he"
                                    ? "right"
                                    : "left",
                              }}
                              disabled={
                                selectedCard === 2 || selectedCard === 3
                              }
                            />
                          </div>
                          {selectedCard === 1 && (
                            <>
                              <div className="mt-3 rounded-xl border border-gray-200 dark:border-gray-600 p-3">
                                <div className="flex items-center justify-between gap-3 text-xs text-gray-900 dark:text-gray-100">
                                  <span>Set as default template</span>
                                  <button
                                    type="button"
                                    role="switch"
                                    aria-checked={!!templateSettings?.isDefault}
                                    onClick={() =>
                                      canManageDefaultTemplates &&
                                      updateTemplateSettings(
                                        "isDefault",
                                        !templateSettings?.isDefault
                                      )
                                    }
                                    disabled={!canManageDefaultTemplates}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                      !!templateSettings?.isDefault
                                        ? "bg-[#CBEA7B] dark:bg-[#A4C639]"
                                        : "bg-gray-300 dark:bg-gray-600"
                                    } ${
                                      !canManageDefaultTemplates
                                        ? "opacity-50 cursor-not-allowed"
                                        : "cursor-pointer"
                                    }`}
                                  >
                                    <span
                                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                        !!templateSettings?.isDefault
                                          ? "translate-x-6"
                                          : "translate-x-1"
                                      }`}
                                    />
                                  </button>
                                </div>
                                {!canManageDefaultTemplates && (
                                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                                    Admin only
                                  </p>
                                )}
                              </div>
                              <div className="mt-3 rounded-xl border border-gray-200 dark:border-gray-600 p-3">
                                <div className="flex items-center justify-between gap-3 text-xs text-gray-900 dark:text-gray-100">
                                  <span>{t("loadDsrData")}</span>
                                  <button
                                    type="button"
                                    role="switch"
                                    aria-checked={!!templateSettings?.loadDsrData}
                                    onClick={() =>
                                      updateTemplateSettings(
                                        "loadDsrData",
                                        !templateSettings?.loadDsrData,
                                      )
                                    }
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                                      !!templateSettings?.loadDsrData
                                        ? "bg-[#CBEA7B] dark:bg-[#A4C639]"
                                        : "bg-gray-300 dark:bg-gray-600"
                                    }`}
                                  >
                                    <span
                                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                        !!templateSettings?.loadDsrData
                                          ? "translate-x-6"
                                          : "translate-x-1"
                                      }`}
                                    />
                                  </button>
                                </div>
                                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                                  {t("loadDsrDataHelp")}
                                </p>
                              </div>
                            </>
                          )}
                          {errors.name && (
                            <div className="text-red-500 dark:text-red-400 text-xs">
                              {errors.name}
                            </div>
                          )}
                          {(selectedCard === 0 || selectedCard === 1) &&
                            errors.content && (
                              <div className="text-red-500 dark:text-red-400 text-xs mt-1">
                                {errors.content}
                              </div>
                            )}
                          <button
                            onClick={
                              selectedCard === 2
                                ? handleSaveHeader
                                : selectedCard === 3
                                ? handleSaveFooter
                                : selectedCard === 0
                                ? handleSaveMacro
                                : handleSaveTemplate
                            }
                            className="w-full mt-3 bg-[#CBEA7B80] dark:bg-[#A4C63980] h-12 font-poppins rounded-full flex items-center justify-center text-gray-900 dark:text-gray-100 hover:bg-[#CBEA7B] dark:hover:bg-[#A4C639] transition-colors"
                            disabled={saving}
                          >
                            {saving ? (
                              <div
                                className="animate-spin inline-block size-6 border-[3px] border-current border-t-transparent text-blue-600 dark:text-blue-400 rounded-full"
                                role="status"
                                aria-label="loading"
                              >
                                <span className="sr-only">Loading...</span>
                              </div>
                            ) : selectedCard === 2 ? (
                              t('saveHeader')
                            ) : selectedCard === 3 ? (
                              t('saveFooter')
                            ) : selectedCard === 0 ? (
                              isMacroEditing ? (
                                t('updateMacro')
                              ) : (
                                t('saveMacro')
                              )
                            ) : isEditing ? (
                              t('update')
                            ) : (
                              t('save')
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </DraggableSection>
              );
            } else if (section === "anathomy" && selectedCard === 1) {
              // Single anatomy selection - value is a single code string
              const selectedAnathomyValue = anathomy || "";

              // Handle onChange from Select component - single value (string)
              const handleAnathomyChange = (selectedValue) => {
                // Set to empty string if no selection or invalid value
                if (!selectedValue || selectedValue === "") {
                  setAnathomy("");
                  return;
                }
                // Store single anatomy code as string
                setAnathomy(String(selectedValue).trim());
              };

              return (
                <DraggableSection key={section} id={section}>
                  {(listeners) => (
                    <div className="relative p-2 sm:p-3 bg-white dark:bg-gray-800 rounded-2xl font-poppins shadow-sm dark:shadow-gray-700">
                      <div
                        className={dragHandleClass}
                        {...listeners}
                        style={{ touchAction: 'none' }}
                        title={t('dragToReorder') || 'Drag to reorder'}
                        role="button"
                        aria-label={t('dragToReorder') || 'Drag to reorder'}
                      >
                        {dragHandleSvg}
                      </div>
                      {loading ? (
                        <Skeleton height={100} />
                      ) : (
                        <>
                          <div className="flex gap-3 mb-3">
                            <div className="w-11 h-11 bg-gray-200 dark:bg-gray-700 rounded-full flex justify-center items-center">
                              <FaStethoscope className="w-5 h-5 text-gray-900 dark:text-gray-100" />
                            </div>
                            <div className="text-xs text-gray-900 dark:text-gray-100">
                              <h1 className="font-bold">{t('anathomy')}</h1>
                              <p>{t('selectAnathomyType')}</p>
                            </div>
                          </div>
                          <Select
                            label={t('anathomy')}
                            value={selectedAnathomyValue}
                            options={anathomies
                              .filter((item) => item.id !== -1) // Exclude the "<ALL>" option
                              .map((item) => ({
                                value: item.code,
                                label: item.name,
                              }))}
                            onChange={handleAnathomyChange}
                            isMultiple={false}
                            disabled={selectedCard === 2 || selectedCard === 3}
                          />
                        </>
                      )}
                    </div>
                  )}
                </DraggableSection>
              );
            } else if (section === "addField") {
              return (
                <DraggableSection key={section} id={section}>
                  {(listeners) => (
                    <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-2 sm:p-3 shadow-sm dark:shadow-gray-700">
                      <div
                        className={dragHandleClass}
                        {...listeners}
                        style={{ touchAction: 'none' }}
                        title={t('dragToReorder') || 'Drag to reorder'}
                        role="button"
                        aria-label={t('dragToReorder') || 'Drag to reorder'}
                      >
                        {dragHandleSvg}
                      </div>
                      {loading ? (
                        <Skeleton height={150} />
                      ) : (
                        <>
                          <div className="flex gap-2 justify-between">
                            <div className="flex gap-2">
                              <div className="w-11 h-11 bg-gray-200 dark:bg-gray-700 rounded-full flex justify-center items-center">
                                <img
                                  src={copy}
                                  alt=""
                                  className="w-5 h-5 dark:invert"
                                />
                              </div>
                              <div className="text-xs text-gray-900 dark:text-gray-100">
                                <h1 className="font-bold">{t('addFields')}</h1>
                                <p>{t('selectFieldsFromTable')}</p>
                              </div>
                            </div>
                          </div>

                          <div className="relative space-y-1 blur-overlay blur-bottom h-96 custom-scrollbar mobile-scroll">
                            <div className="h-96">
                              {catalogTableName.length > 0 && (
                                <>
                                  <TableDropdown
                                    label={t('tableName')}
                                    value={selectedTable}
                                    options={catalogTableName}
                                    onChange={(e) =>
                                      setSelectedTable(e.target.value)
                                    }
                                  />
                                  <div className="relative">
                                    <input
                                      type="text"
                                      dir={
                                        language === "ar" || language === "he"
                                          ? "rtl"
                                          : "ltr"
                                      }
                                      value={searchQuery}
                                      onChange={(e) =>
                                        setSearchQuery(e.target.value)
                                      }
                                      placeholder={t('searchTableItems')}
                                      className="w-[95%] ml-2 border border-gray-300 dark:border-gray-600 h-12 p-3 rounded-full mt-3 mb-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-400 dark:focus:ring-green-600"
                                      style={{
                                        textAlign:
                                          language === "ar" || language === "he"
                                            ? "right"
                                            : "left",
                                      }}
                                    />
                                    <IoSearchSharp className="absolute top-7 w-5 h-5 right-6 text-gray-500 dark:text-gray-400" />
                                  </div>
                                </>
                              )}
                              <div className="overflow-y-scroll h-60 mobile-scroll">
                                {filteredItems.map((item, index) => (
                                  <div
                                    key={index}
                                    style={{ touchAction: 'pan-y' }}
                                    className="w-full flex gap-2 place-content-center items-center h-auto p-3 rounded-2xl mt-3 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                                    onClick={() =>
                                      selectedCard === 0
                                        ? handleInsertAtCursor(
                                            item.table_code,
                                            "macro"
                                          )
                                        : selectedCard === 1
                                        ? handleInsertAtCursor(
                                            item.table_code,
                                            "template"
                                          )
                                        : selectedCard === 2
                                        ? handleInsertAtCursor(
                                            item.table_code,
                                            "header"
                                          )
                                        : selectedCard === 3
                                        ? handleInsertAtCursor(
                                            item.table_code,
                                            "footer"
                                          )
                                        : onDoubleClickItem(item.table_code)
                                    }
                                  >
                                    <div className="w-8 h-8 place-content-center justify-items-center bg-black dark:bg-gray-600 rounded-full">
                                      <img
                                        src={dot}
                                        alt=""
                                        className="w-2 h-3"
                                      />
                                    </div>
                                    <div className="w-11/12">
                                      <h1 className="text-[#828282] dark:text-gray-300 text-md">
                                        {item.table_code}
                                      </h1>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white dark:from-gray-800 to-transparent pointer-events-none"></div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </DraggableSection>
              );
            }
            return null;
          })}
        </div>
      </SortableContext>
      <ConfirmModal
        isVisible={showNewTemplateConfirm}
        title={t("newTemplate")}
        message={t("newTemplateConfirm")}
        confirmText={t("discard")}
        cancelText={t("cancel")}
        variant="danger"
        onConfirm={handleNewTemplateConfirm}
        onCancel={handleNewTemplateCancel}
      />
    </DndContext>
  );
};

export default Sidebar;
